/* eslint-disable no-else-return, curly, no-magic-numbers, no-useless-return, no-unused-expressions */
// UI
import animate from 'ui/animate';
import { easeOutSine } from 'ui/easings';
import ResizeTracker from 'utils/ResizeTracker';

/**
 * @module Accordion
 */
class Accordion {

    /**
     * Class constructor
     *
     * @param {HTMLElement} element - The HTMLElement this module is constructed upon
     * @param {Object} options - ConditionerJS's merged options
     */
    constructor(element, options) {
        this._element = element;

        this._options = {
            ...Accordion.options,
            ...options,
            mobileSize: 768
        };

        this._state = {
            isOpen: false,
            isMobile: false,
            init: false,
            expandedElements: []
        };

        this._rootContainer = document.body;
        this._state.isOpen = this._options.isOpen === 'true';
        this._setIsMobile = this._setIsMobile.bind(this);
        this._init();
    }

    /**
     * Closes all the nested Accordions within this Accordion
     * @private
     */
    _closeNestedAccordions() {
        const nestedAccordions = Array.from(
            this._element.querySelectorAll('[data-accordion]')
        );
        nestedAccordions.forEach(nestedAccordion =>
            nestedAccordion.setAttribute('data-is-open', false)
        );
    }

    /**
     * Animate container
     * @param {Number} from - Starting point
     * @param {Number} to - Ending point
     * @private
     */
    _animate(from, to) {

        animate({
            from,
            to,
            cb: progress => {
                this._elements.content.style.maxHeight = `${progress}px`;
            },
            duration: 250,
            ease: easeOutSine
        }).then(() => {
            this._elements.content.removeAttribute('style');

            if (!this._state.isOpen) {
                this._closeNestedAccordions();
            }
        })
            .catch(() => false);
    }

    /**
     * Get content container height
     * @returns {Object} - Height of the content container
     * @private
     */
    _getContentHeight() {
        const clone = this._elements.content.cloneNode(true);
        clone.setAttribute('data-is-open', true);

        this._element.insertAdjacentElement('beforeend', clone);
        const height = clone.scrollHeight;

        clone.parentNode.removeChild(clone);

        return height;
    }


    /**
     * Toggle content
     * @private
     */
    _toggleContent() {
        this._state.isOpen = !this._state.isOpen;
        this._toggleElements();
        this._elements.trigger.forEach(trigger => {
            trigger.setAttribute('aria-expanded', this._state.isOpen);
        });
        this._element.setAttribute('data-is-open', this._state.isOpen);
        const contentElement = this._element.querySelector('[data-content]');
        contentElement && contentElement.setAttribute('aria-hidden', !this._state.isOpen);

        if (this._state.isOpen) {
            this._animate(0, this._getContentHeight());
        } else if (!this._state.isOpen) {
            this._animate(this._getContentHeight(), 0);
        }
    }

    /**
     * Toggle elements
     * @private
     */
    _toggleElements() {
        if (!this._state.isOpen || !this._options.toggleElements) return;
        this._elements.toggleElements.forEach(element => {
            const isOpen = element.getAttribute('data-is-open') === 'true';
            if (!isOpen) return;
            element.setAttribute('data-is-open', false);
            const triggerElement = element.querySelector(this._options.triggerElements);
            const contentElement = element.querySelector('[data-content]');

            contentElement && contentElement.setAttribute('aria-hidden', true);
            triggerElement && triggerElement.setAttribute('aria-expanded', false);
            this._animate(this._getContentHeight(), 0);
        });
    }


    /**
     * Expand elements that were open before page refresh
     * @private
     */
    _expandElements() {
        const expandedElements = window.sessionStorage.getItem('expandedElements');
        window.sessionStorage.removeItem('expandedElements');

        if (!expandedElements) return;
        this._state.expandedElements = JSON.parse(expandedElements);
        for (const id of this._state.expandedElements) {
            const element = document.getElementById(id);
            element.setAttribute('data-is-open', 'true');
        }
    }

    /**
     * Setup mutation observer for Accordion
     * @private
     */
    _setupMutationObserver() {
        const callback = mutationsList => {
            mutationsList.forEach(mutation => {
                const isOpenAttributeChanged =
                    mutation.type === 'attributes' &&
                    mutation.attributeName === 'data-is-open';
                if (isOpenAttributeChanged) {
                    this._state.isOpen =
                        this._element.getAttribute('data-is-open') === 'true';
                }
            });
        };

        const _observer = new MutationObserver(callback);
        _observer.observe(this._element, { attributes: true });
        this._state.init = true;
    }

    /**
     * Add all needed event listeners
     * @private
     */
    _addEventListeners() {
        this._elements.trigger.forEach(trigger => {
            this._options.triggerParent !== 'true' && trigger.addEventListener('click', this._toggleContent.bind(this));
            this._options.triggerParent === 'true' && !this._elements.triggerParent && trigger.parentNode.addEventListener('click', this._toggleContent.bind(this));
        });
        this._options.triggerParent === 'true' && this._elements.triggerParent && this._elements.triggerParent.addEventListener('click', this._toggleContent.bind(this));
    }

    /**
     * Add all needed event listeners
     * @private
     */
    _addResizeListener() {
        !this._options.mobile && this._addEventListeners();
        !this._options.mobile && this._setupMutationObserver();
        if (!this._options.mobile) return;
        this._resizeTracker = new ResizeTracker();

        this._resizeTracker.init(
            { element: this._rootContainer },
            this._setIsMobile
        );
    }

    /**
     * Remove all needed event listeners
     * @private
     */
    _removeEventListeners() {
        this._elements.trigger.forEach(trigger => {
            this._options.triggerParent !== 'true' && trigger.removeEventListener('click', this._toggleContent);
            this._options.triggerParent === 'true' && !this._elements.triggerParent && trigger.parentNode.removeEventListener('click', this._toggleContent);
        });
        this._options.triggerParent === 'true' && this._elements.triggerParent && this._elements.triggerParent.removeEventListener('click', this._toggleContent);
        this._state.init = false;
    }

    /**
     * Set mobile state and start listeners
     * @private
     */
    _setIsMobile() {

        this._state.isMobile = this._rootContainer.innerWidth < this._options.mobileSize;
        !this._options.mobile && this._state.init && this._removeEventListeners();
        this._options.mobile && !this._state.init && this._addEventListeners();
        this._options.mobile && !this._state.init && this._setupMutationObserver();
    }

    /**
     * Cache all needed elements for further use
     * @private
     */
    _cacheSelectors() {
        this._elements = {
            toggleElements: this._options.toggleElements && [...this._element.parentNode.querySelectorAll(this._options.toggleElements)],
            trigger: [...this._element.querySelectorAll(this._options.triggerElements)],
            content: this._element.querySelector('[data-content]'),
            triggerParent: this._options.triggerParentElement && this._element.querySelector(this._options.triggerParentElement)
        };
    }

    /**
     * Initialize the component
     * @private
     */
    _init() {
        this._cacheSelectors();
        this._expandElements();
        this._addResizeListener();
    }
}

export default Accordion;
/* eslint-enable */

